const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

class BuildingSetUpsService {
  /**
   * Get building set ups with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response
   */
  async getBuildingSetUps(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append("status", params.status);
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_dir) queryParams.append("sort_dir", params.sort_dir);

      const url = `${API_BASE_URL}/api/building-set-ups?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Building Set Ups Service getBuildingSetUps error:", error);
      // Return mock data if API fails
      return this.getMockData(params);
    }
  }

  /**
   * Update building set up entry status
   * @param {string} entryId - Entry ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} API response
   */
  async updateEntry(entryId, data) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/${entryId}`;

      // Convert status format to match Laravel (hyphenated)
      const formattedData = { ...data };
      if (formattedData.status === "in_progress") {
        formattedData.status = "in-progress";
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        // Surface the real backend error instead of pretending it succeeded.
        let message = `Request failed (status ${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch (_) {
          /* response had no JSON body */
        }
        return { success: false, message };
      }

      return await response.json();
    } catch (error) {
      console.error("Building Set Ups Service updateEntry error:", error);
      // Do NOT fake success here — a failed update must report failure so the
      // user isn't told an entry was completed when it was not.
      return {
        success: false,
        message: "Could not reach the server. Please try again.",
      };
    }
  }

  /**
   * Manually add a property to the Building Set Up module (ticket row 336).
   * @param {number} propertyId - Property to add
   * @param {boolean} reopen - If true, re-open an already-completed entry
   * @returns {Promise<Object>} { httpStatus, success, status, code, message, completed_at, data }
   */
  async addEntry(propertyId, reopen = false) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/entries`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ property_id: propertyId, reopen }),
      });

      let body = {};
      try {
        body = await response.json();
      } catch (_) {
        /* no JSON body */
      }
      // Surface the HTTP status so the caller can detect 409 already_completed.
      return { httpStatus: response.status, ...body };
    } catch (error) {
      console.error("Building Set Ups Service addEntry error:", error);
      return {
        httpStatus: 0,
        success: false,
        message: "Could not reach the server. Please try again.",
      };
    }
  }

  /**
   * Search properties for the "Add to BSU" picker.
   * @param {string} query
   * @returns {Promise<Array>} Array of { id, property_id, name, address_1, postcode }
   */
  async searchProperties(query) {
    try {
      const url = `${API_BASE_URL}/api/properties/search?q=${encodeURIComponent(
        query
      )}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) return [];
      const json = await response.json();
      return json.properties || [];
    } catch (error) {
      console.error("Building Set Ups Service searchProperties error:", error);
      return [];
    }
  }

  /**
   * Update requirement status and due date
   * @param {string} entryId - Entry ID
   * @param {string} requirementType - Requirement type
   * @param {Object} data - Update data
   * @returns {Promise<Object>} API response
   */
  async updateRequirement(entryId, requirementType, data) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/${entryId}/requirement/${requirementType}`;

      // Convert status format to match Laravel (hyphenated)
      const formattedData = { ...data };
      if (formattedData.status === "not_met") {
        formattedData.status = "not-met";
      }
      if (formattedData.status === "not_applicable") {
        formattedData.status = "not-applicable";
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        // Report the real failure. Faking success here told staff a column was
        // saved when it was not, and the value then vanished on the next load.
        let message = `Request failed (status ${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch (_) {
          /* response had no JSON body */
        }
        return { success: false, message };
      }

      return await response.json();
    } catch (error) {
      console.error("Building Set Ups Service updateRequirement error:", error);
      return {
        success: false,
        message: "Could not reach the server. Please try again.",
      };
    }
  }

  /**
   * Fetch the list of users allowed for the Assigned To dropdown.
   * @returns {Promise<Array<{id:number,name:string}>>}
   */
  async getAssignees() {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/assignees/list`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      return json.data || [];
    } catch (error) {
      console.error("Building Set Ups Service getAssignees error:", error);
      return [];
    }
  }

  /**
   * A property's finished Building Set Up rounds, for the history panel on the
   * property page.
   * @param {number} propertyId
   * @returns {Promise<Array>}
   */
  async getRoundHistory(propertyId) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/history/${propertyId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      return json.data || [];
    } catch (error) {
      console.error("Building Set Ups Service getRoundHistory error:", error);
      return [];
    }
  }

  /**
   * Tickets a staff member can choose for one BSU column, for the "change
   * linked ticket" picker.
   * @param {number} entryId - Property ID of the BSU entry
   * @param {string} column - Column key, e.g. 'gas_safe_certificate'
   * @returns {Promise<Array>}
   */
  async getTicketCandidates(entryId, column) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/${entryId}/ticket-candidates/${column}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      return json.data || [];
    } catch (error) {
      console.error(
        "Building Set Ups Service getTicketCandidates error:",
        error
      );
      return [];
    }
  }

  /**
   * Link a column to a ticket by hand, detach it, or hand it back to the
   * automatic match.
   * @param {number} entryId - Property ID of the BSU entry
   * @param {string} column - Column key
   * @param {Object} body - { ticket_id } | { ticket_id: null } | { reset: true }
   * @returns {Promise<Object>}
   */
  async setTicketLink(entryId, column, body) {
    try {
      const url = `${API_BASE_URL}/api/building-set-ups/${entryId}/ticket-link/${column}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          message: json?.message || `Request failed (status ${response.status})`,
        };
      }
      return json;
    } catch (error) {
      console.error("Building Set Ups Service setTicketLink error:", error);
      return { success: false, message: "Failed to update the linked ticket" };
    }
  }

  /**
   * Export building set ups to CSV
   * @param {Object} params - Export parameters
   * @returns {Promise<void>}
   */
  async exportCsv(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append("status", params.status);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_dir) queryParams.append("sort_dir", params.sort_dir);

      const url = `${API_BASE_URL}/api/building-set-ups/export/csv?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url2;
      a.download = "building-set-ups.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Building Set Ups Service exportCsv error:", error);
      // Create mock CSV download if API fails
      this.createMockCsvDownload(params);
    }
  }

  /**
   * Get mock data for testing
   * @param {Object} params - Query parameters
   * @returns {Object} Mock response
   */
  getMockData(params = {}) {
    const mockEntries = [
      {
        id: 1,
        property_id: "PROP001",
        status: "new",
        property: {
          id: 1,
          property_id: "PROP001",
          name: "12 Savage Road",
          address_1: "12 Savage Road",
          postcode: "PL5 1BP",
          status_text: "Active",
        },
        requirements: [
          {
            id: 1,
            type: "keys",
            status: "not_met",
            due_date: "2025-02-15",
            label: "Keys",
          },
          {
            id: 2,
            type: "set-ups",
            status: "not_met",
            due_date: "2025-02-20",
            label: "Set Ups",
          },
          {
            id: 3,
            type: "240V_certificate",
            status: "met",
            due_date: "2025-01-30",
            label: "240V Certificate",
          },
        ],
        target_date: "2025-02-15",
        created_at: "2025-01-15T10:00:00.000Z",
        updated_at: "2025-01-15T10:00:00.000Z",
      },
      {
        id: 2,
        property_id: "PROP002",
        status: "in_progress",
        property: {
          id: 2,
          property_id: "PROP002",
          name: "35 University Road",
          address_1: "35 University Road",
          postcode: "PO1 2DZ",
          status_text: "Active",
        },
        requirements: [
          {
            id: 4,
            type: "gas_safe_certificate",
            status: "in_progress",
            due_date: "2025-02-10",
            label: "Gas Safe Certificate",
          },
          {
            id: 5,
            type: "fire_risk_assessment",
            status: "met",
            due_date: "2025-01-25",
            label: "Fire Risk Assessment",
          },
        ],
        target_date: "2025-02-10",
        created_at: "2025-01-10T10:00:00.000Z",
        updated_at: "2025-01-12T10:00:00.000Z",
      },
      {
        id: 3,
        property_id: "PROP003",
        status: "completed",
        property: {
          id: 3,
          property_id: "PROP003",
          name: "1 Bowie Close",
          address_1: "1 Bowie Close",
          postcode: "SW4 8HA",
          status_text: "Active",
        },
        requirements: [
          {
            id: 6,
            type: "keys",
            status: "met",
            due_date: "2025-01-20",
            label: "Keys",
          },
          {
            id: 7,
            type: "set-ups",
            status: "met",
            due_date: "2025-01-22",
            label: "Set Ups",
          },
          {
            id: 8,
            type: "nic_certification",
            status: "met",
            due_date: "2025-01-18",
            label: "NIC Certificate",
          },
        ],
        target_date: "2025-01-18",
        created_at: "2025-01-05T10:00:00.000Z",
        updated_at: "2025-01-20T10:00:00.000Z",
      },
    ];

    // Apply status filter
    let filteredEntries = mockEntries;
    if (params.status && params.status !== "all") {
      filteredEntries = mockEntries.filter(
        (entry) => entry.status === params.status
      );
    }

    // Apply search filter
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filteredEntries = filteredEntries.filter(
        (entry) =>
          entry.property?.name?.toLowerCase().includes(searchLower) ||
          entry.property?.property_id?.toLowerCase().includes(searchLower) ||
          entry.property?.address_1?.toLowerCase().includes(searchLower)
      );
    }

    // Apply pagination
    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedEntries = filteredEntries.slice(startIndex, endIndex);

    const stats = {
      total: mockEntries.length,
      new: mockEntries.filter((e) => e.status === "new").length,
      in_progress: mockEntries.filter((e) => e.status === "in_progress").length,
      completed: mockEntries.filter((e) => e.status === "completed").length,
    };

    return {
      success: true,
      data: paginatedEntries,
      stats: stats,
      pagination: {
        current_page: page,
        per_page: limit,
        total: filteredEntries.length,
        total_pages: Math.ceil(filteredEntries.length / limit),
      },
      message: `Found ${paginatedEntries.length} building set up entries (Mock data)`,
    };
  }

  /**
   * Create mock CSV download
   * @param {Object} params - Export parameters
   */
  createMockCsvDownload(params = {}) {
    const csvData = `"Property ID","Property Name","Address","Postcode","Status","Target Date","Requirements"
"PROP001","12 Savage Road","12 Savage Road","PL5 1BP","new","2025-02-15","Keys, Set Ups, 240V Certificate"
"PROP002","35 University Road","35 University Road","PO1 2DZ","in_progress","2025-02-10","Gas Safe Certificate, Fire Risk Assessment"
"PROP003","1 Bowie Close","1 Bowie Close","SW4 8HA","completed","2025-01-18","Keys, Set Ups, NIC Certificate"`;

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "building-set-ups.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export default new BuildingSetUpsService();
